import { Bet } from '@/models/host/BetAmount';
import { BaccaratBetsHistory } from '@/modules/games/slice';
import { RootState } from '@/store/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const BACCARAT_HISTORY_LIMIT: number = 50;

type TableBetInfo = {
    hostId: number;
    totalBetAmount: number;
    totalWithHoldAmount: number;
    pendingBetAmount: number;
    pendingWithHoldAmount: number;
};
type BaccaratBetsHistoryPayload = {
    hostId: number;
    gameId: number;
    betsSummary: Array<number>;
};
export type GameLiveDataState = {
    currentGameId: number;
    currentGameState: number;
    waitingBetResult: boolean;
    hostsBetInfo: Array<TableBetInfo>;
    totalHostsBetInfo: TableBetInfo;
    baccaratBetsHistory: Array<BaccaratBetsHistory>;
    totalBaccaratBetAmount: number; //For multibet total display
    openTable: Array<number>;
};
const initialLiveDataState = {
    currentGameId: -1,
    currentGameState: -1,
    waitingBetResult: false,
    openTable: [] as number[],
} as GameLiveDataState;

const setWaitingBetResult = (
    state: GameLiveDataState,
    action: PayloadAction<boolean>
) => {
    if (state.waitingBetResult != action.payload)
        state.waitingBetResult = action.payload;
};

const updateHostBetInfo = (
    state: GameLiveDataState,
    action: PayloadAction<TableBetInfo>
) => {
    let total = {
        hostId: 0,
        totalBetAmount: 0,
        totalWithHoldAmount: 0,
        pendingBetAmount: 0,
        pendingWithHoldAmount: 0,
    } as TableBetInfo;
    let tmpList = state.hostsBetInfo ? [...state.hostsBetInfo] : [];
    const idx = tmpList.findIndex(
        item => item.hostId === action.payload.hostId
    );
    if (action.payload.totalBetAmount === 0) {
        if (idx >= 0) {
            tmpList.splice(idx, 1);
        }
    } else {
        if (idx < 0) {
            tmpList.push(action.payload);
        } else {
            tmpList[idx].totalBetAmount = action.payload.totalBetAmount;
            tmpList[idx].pendingBetAmount = action.payload.pendingBetAmount;
            tmpList[idx].totalWithHoldAmount =
                action.payload.totalWithHoldAmount;
            tmpList[idx].pendingWithHoldAmount =
                action.payload.pendingWithHoldAmount;
        }
    }
    tmpList.forEach(v => {
        total.totalBetAmount += v.totalBetAmount;
        total.pendingBetAmount += v.pendingBetAmount;
        total.totalWithHoldAmount += v.totalWithHoldAmount;
        total.pendingWithHoldAmount += v.pendingWithHoldAmount;
    });
    if (JSON.stringify(state.hostsBetInfo) !== JSON.stringify(tmpList)) {
        state.hostsBetInfo = tmpList;
    }
    if (JSON.stringify(state.totalHostsBetInfo) !== JSON.stringify(total)) {
        state.totalHostsBetInfo = total;
    }
};

const updateBaccaratBetHistory = (
    state: GameLiveDataState,
    action: PayloadAction<BaccaratBetsHistoryPayload>
) => {
    let tmpList = state.baccaratBetsHistory
        ? [...state.baccaratBetsHistory]
        : [];

    let hostBets = tmpList.filter(v => v.gameId === action.payload.gameId);
    let insertedBetType = new Array<number>();
    hostBets.forEach(v => {
        if (!v.isResultRelease) {
            if (!action.payload.betsSummary[v.betType]) {
                let idx = tmpList.indexOf(v);
                if (idx >= 0) {
                    tmpList.splice(idx, 1);
                }
            } else {
                v.betAmount = action.payload.betsSummary[v.betType];
                insertedBetType.push(v.betType);
            }
        } else {
            insertedBetType.push(v.betType);
        }
    });
    action.payload.betsSummary.forEach((amount, index) => {
        if (insertedBetType.indexOf(index) < 0 && amount) {
            tmpList.push({
                hostId: action.payload.hostId,
                gameId: action.payload.gameId,
                betType: index,
                betAmount: amount,
                isResultRelease: false,
                isCancel: false,
                winAmount: 0,
            });
        }
    });

    let total = 0;
    tmpList.forEach(v => {
        if (!v.isResultRelease) {
            total += v.betAmount;
        }
    });
    const newList = tmpList.splice(
        Math.max(tmpList.length - BACCARAT_HISTORY_LIMIT, 0)
    );
    if (JSON.stringify(newList) !== JSON.stringify(state.baccaratBetsHistory))
        state.baccaratBetsHistory = newList;
    if (total !== state.totalBaccaratBetAmount)
        state.totalBaccaratBetAmount = total;
};
const updateBaccaratBetHistoryCancel = (
    state: GameLiveDataState,
    action: PayloadAction<number>
) => {
    let tmpList = state.baccaratBetsHistory
        ? [...state.baccaratBetsHistory]
        : [];

    let hostBets = tmpList.filter(v => v.gameId === action.payload);
    hostBets.forEach(v => {
        v.isCancel = true;
        v.isResultRelease = true;
    });

    let total = 0;
    tmpList.forEach(v => {
        if (!v.isResultRelease) {
            total += v.betAmount;
        }
    });
    const newList = tmpList.splice(
        Math.max(tmpList.length - BACCARAT_HISTORY_LIMIT, 0)
    );
    if (JSON.stringify(state.baccaratBetsHistory) !== JSON.stringify(newList))
        state.baccaratBetsHistory = newList;
    if (state.totalBaccaratBetAmount !== total)
        state.totalBaccaratBetAmount = total;
};

const updateBaccaratBetHistoryPayout = (
    state: GameLiveDataState,
    action: PayloadAction<Array<BaccaratBetsHistory>>
) => {
    let tmpList = state.baccaratBetsHistory
        ? [...state.baccaratBetsHistory]
        : [];

    action.payload.forEach(his => {
        let idx = tmpList.findIndex(
            item => item.gameId === his.gameId && item.betType === his.betType
        );
        if (his.isResultRelease) {
            if (idx >= 0) {
                tmpList[idx].winAmount = his.winAmount;
                tmpList[idx].isResultRelease = his.isResultRelease;
            }
        }
    });
    let total = 0;
    tmpList.forEach(v => {
        if (!v.isResultRelease) {
            total += v.betAmount;
        }
    });
    if (JSON.stringify(state.baccaratBetsHistory) !== JSON.stringify(tmpList))
        state.baccaratBetsHistory = tmpList;
    if (state.totalBaccaratBetAmount !== total)
        state.totalBaccaratBetAmount = total;
};

const updateOpenTable = (
    state: GameLiveDataState,
    action: PayloadAction<Array<number>>
) => {
    state.openTable = action.payload;
};
const setCurrentGame = (
    state: GameLiveDataState,
    action: PayloadAction<{
        gameId: number;
        // bets: Array<Bet>;
    }>
) => {
    const { gameId } = action.payload;

    if (state.currentGameId !== gameId) {
        // state.confirmedBets = bets;
        state.waitingBetResult = false;
        state.currentGameId = gameId;
    }
};
const onConfirmedBetResult = (
    state: GameLiveDataState,
    action: PayloadAction<{
        gameId: number;
        bet: Array<Bet>;
    }>
) => {
    const { gameId } = action.payload;
    if (gameId == state.currentGameId) {
        // state.prevBets = state.confirmedBets = action.payload.bet;

        // Reset pending bet
        state.waitingBetResult = false;
    }
};

const setCurrentGameState = (
    state: GameLiveDataState,
    action: PayloadAction<{ gameId: number; gameState: number }>
) => {
    const { gameId, gameState } = action.payload;

    if (state.currentGameId === gameId) {
        state.currentGameState = gameState;
        state.waitingBetResult = false;
    }
};
export const getLiveDataState = (state: RootState) =>
    state[GAME_LIVE_KEY] as GameLiveDataState;

export const getCurrentGameId = (state: RootState): number => {
    const gameState = getLiveDataState(state);
    return gameState.currentGameId;
};
export const getWaitingBetResult = (state: RootState): boolean => {
    const gameState = getLiveDataState(state);
    return gameState.waitingBetResult;
};

export const GAME_LIVE_KEY = 'app::game::live::data';
const liveDataSlice = createSlice({
    name: GAME_LIVE_KEY,
    initialState: initialLiveDataState,
    reducers: {
        onConfirmedBetResult,
        setCurrentGame,
        setCurrentGameState,
        setWaitingBetResult,
        updateHostBetInfo,
        updateBaccaratBetHistory,
        updateBaccaratBetHistoryPayout,
        updateBaccaratBetHistoryCancel,
        updateOpenTable,
    },
});
export const LiveDataSliceReducer = liveDataSlice.reducer;
export const LiveDataSliceActions = liveDataSlice.actions;
